import React from 'react';
import { useLlmProvider, setLlmProvider, type LlmProvider } from '@hooks/useLlmProvider';

type LLMProvider = LlmProvider;

export function LLMProviderToggle(): React.ReactElement {
  const provider = useLlmProvider();

  function handleSelect(p: LLMProvider): void {
    setLlmProvider(p);
  }

  return (
    <nav className="flex gap-0.5 bg-slate-900/60 rounded-md p-0.5 border border-slate-800">
      {/* Gemini */}
      <button
        onClick={() => handleSelect('gemini')}
        title="Google Gemini"
        className={`flex items-center gap-1 px-1.5 py-1 rounded transition-all duration-200 cursor-pointer ${
          provider === 'gemini'
            ? 'bg-blue-500/15 border border-blue-500/50 shadow-[0_0_8px_rgba(59,130,246,0.25)]'
            : 'border border-transparent hover:bg-slate-800/60'
        }`}
      >
        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 flex-shrink-0" fill="none">
          <path d="M12 24C12 22.3431 11.6831 20.7013 11.0667 19.1667C10.4503 17.632 9.5474 16.234 8.3935 15.0416C7.2396 13.8492 5.8491 12.8861 4.3138 12.2072C2.7784 11.5284 1.125 11.149 0 11.0333V12.9667C1.125 12.851 2.7784 12.4716 4.3138 11.7928C5.8491 11.1139 7.2396 10.1508 8.3935 8.9584C9.5474 7.766 10.4503 6.368 11.0667 4.8333C11.6831 3.2987 12 1.6569 12 0C12 1.6569 12.3169 3.2987 12.9333 4.8333C13.5497 6.368 14.4526 7.766 15.6065 8.9584C16.7604 10.1508 18.1509 11.1139 19.6862 11.7928C21.2216 12.4716 22.875 12.851 24 12.9667V11.0333C22.875 11.149 21.2216 11.5284 19.6862 12.2072C18.1509 12.8861 16.7604 13.8492 15.6065 15.0416C14.4526 16.234 13.5497 17.632 12.9333 19.1667C12.3169 20.7013 12 22.3431 12 24Z" fill={provider === 'gemini' ? '#60a5fa' : 'rgb(var(--color-slate-500))'}/>
        </svg>
        <span className={`text-[11px] font-black uppercase tracking-widest leading-none ${provider === 'gemini' ? 'text-blue-300' : 'text-slate-400'}`}>Gemini</span>
        <span className={`w-1 h-1 rounded-full ${provider === 'gemini' ? 'bg-blue-400 shadow-[0_0_4px_rgba(96,165,250,0.8)]' : 'bg-slate-600'}`} />
      </button>

      {/* Claude */}
      <button
        onClick={() => handleSelect('claude')}
        title="Anthropic Claude"
        className={`flex items-center gap-1 px-1.5 py-1 rounded transition-all duration-200 cursor-pointer ${
          provider === 'claude'
            ? 'bg-orange-500/15 border border-orange-500/50 shadow-[0_0_8px_rgba(249,115,22,0.25)]'
            : 'border border-transparent hover:bg-slate-800/60'
        }`}
      >
        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 flex-shrink-0" fill="none">
          <path d="M17.3541 2L9.56386 21.9097L7.0997 15.4563L0.646484 12.9918L17.3541 2Z" fill={provider === 'claude' ? '#fb923c' : 'rgb(var(--color-slate-500))'}/>
          <path d="M17.3541 2L9.56386 21.9097L12.028 15.4563L23.3541 12.9918L17.3541 2Z" fill={provider === 'claude' ? '#f97316' : 'rgb(var(--color-slate-600))'}/>
        </svg>
        <span className={`text-[11px] font-black uppercase tracking-widest leading-none ${provider === 'claude' ? 'text-orange-300' : 'text-slate-400'}`}>Claude</span>
        <span className={`w-1 h-1 rounded-full ${provider === 'claude' ? 'bg-orange-400 shadow-[0_0_4px_rgba(251,146,60,0.8)]' : 'bg-slate-600'}`} />
      </button>

      {/* OpenAI */}
      <button
        onClick={() => handleSelect('openai')}
        title="OpenAI"
        className={`flex items-center gap-1 px-1.5 py-1 rounded transition-all duration-200 cursor-pointer ${
          provider === 'openai'
            ? 'bg-green-500/15 border border-green-500/50 shadow-[0_0_8px_rgba(34,197,94,0.25)]'
            : 'border border-transparent hover:bg-slate-800/60'
        }`}
      >
        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 flex-shrink-0" fill={provider === 'openai' ? '#4ade80' : 'rgb(var(--color-slate-500))'}>
          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.998 5.998 0 0 0-3.998 2.9 6.042 6.042 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
        </svg>
        <span className={`text-[11px] font-black uppercase tracking-widest leading-none ${provider === 'openai' ? 'text-green-300' : 'text-slate-400'}`}>OpenAI</span>
        <span className={`w-1 h-1 rounded-full ${provider === 'openai' ? 'bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.8)]' : 'bg-slate-600'}`} />
      </button>

      {/* xAI */}
      <button
        onClick={() => handleSelect('xai')}
        title="xAI Grok"
        className={`flex items-center gap-1 px-1.5 py-1 rounded transition-all duration-200 cursor-pointer ${
          provider === 'xai'
            ? 'bg-red-500/15 border border-red-500/50 shadow-[0_0_8px_rgba(239,68,68,0.25)]'
            : 'border border-transparent hover:bg-slate-800/60'
        }`}
      >
        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 flex-shrink-0" fill={provider === 'xai' ? '#f87171' : 'rgb(var(--color-slate-500))'}>
          <path d="M13.982 10.622L20.54 3h-1.554l-5.693 6.618L8.745 3H3.5l6.876 10.007L3.5 21h1.554l6.012-6.989L15.868 21H21.1l-7.118-10.378zm-2.128 2.474l-.697-.997L5.654 4.16h2.386l4.474 6.4.697.996 5.815 8.318h-2.387l-4.745-6.778z"/>
        </svg>
        <span className={`text-[11px] font-black uppercase tracking-widest leading-none ${provider === 'xai' ? 'text-red-300' : 'text-slate-400'}`}>xAI</span>
        <span className={`w-1 h-1 rounded-full ${provider === 'xai' ? 'bg-red-400 shadow-[0_0_4px_rgba(248,113,113,0.8)]' : 'bg-slate-600'}`} />
      </button>
    </nav>
  );
}
